// client/src/coms/views/canvas_parts/canvas_room_view_parts/grabToolUtils.js

const ROTATION_DRAG_SPEED = 0.35;
const GRAB_WHEEL_ZOOM_SPEED = 0.0015;

function normaliseDegrees(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return ((n % 360) + 360) % 360;
}

function radiansToDegrees(value) {
  return (value * 180) / Math.PI;
}

export function getPointerTiltOrientation(source) {
  if (!source || typeof source !== 'object') return null;

  const pointerType = typeof source.pointerType === 'string' ? source.pointerType : '';
  const rawAltitudeAngle = Number(source.altitudeAngle);
  const rawAzimuthAngle = Number(source.azimuthAngle);

  if (pointerType === 'pen' && Number.isFinite(rawAltitudeAngle) && Number.isFinite(rawAzimuthAngle)) {
    const elevation = Math.max(15, Math.min(90, radiansToDegrees(rawAltitudeAngle)));
    const azimuth = normaliseDegrees(radiansToDegrees(rawAzimuthAngle) + 90);
    return { azimuth, elevation, detected: true };
  }

  const rawTiltX = Number(source.tiltX);
  const rawTiltY = Number(source.tiltY);
  if (pointerType !== 'pen' || !Number.isFinite(rawTiltX) || !Number.isFinite(rawTiltY)) return null;

  const tiltMagnitude = Math.hypot(rawTiltX, rawTiltY);
  if (tiltMagnitude <= 0.01) return { azimuth: 0, elevation: 90, detected: true };

  return {
    azimuth: normaliseDegrees(radiansToDegrees(Math.atan2(rawTiltX, -rawTiltY)) + 90),
    elevation: Math.max(15, Math.min(90, 90 - tiltMagnitude)),
    detected: true,
  };
}


export function getPointerSource(evt) {
  if (evt?.touches && evt.touches[0]) return evt.touches[0];
  if (evt?.changedTouches && evt.changedTouches[0]) return evt.changedTouches[0];
  return evt || null;
}

export function getStagePointFromClientPoint(drawAreaEl, stageSize, clientX, clientY) {
  const width = Math.max(1, Number(stageSize?.width) || 1);
  const height = Math.max(1, Number(stageSize?.height) || 1);
  const fallback = { x: width / 2, y: height / 2 };

  if (!drawAreaEl) return fallback;

  const rect = drawAreaEl.getBoundingClientRect();
  if (!rect.width || !rect.height) return fallback;

  const relativeX = (clientX - rect.left) / rect.width;
  const relativeY = (clientY - rect.top) / rect.height;

  return {
    x: relativeX * width,
    y: relativeY * height,
  };
}

export function createCanvasPointFromEvent(evt, getStagePointFromClientPointCallback) {
  const source = getPointerSource(evt);
  if (!source || typeof getStagePointFromClientPointCallback !== 'function') return null;

  const nativeSource = evt && evt.nativeEvent && typeof evt.nativeEvent === 'object' ? evt.nativeEvent : null;
  const pointerSource = nativeSource || source;
  const point = getStagePointFromClientPointCallback(source.clientX, source.clientY);
  const rawPressure = Number(pointerSource.pressure);
  const eventTimeStamp = Number(evt?.timeStamp);
  const nativeTimeStamp = Number(evt?.nativeEvent?.timeStamp);
  const sourceTimeStamp = Number(source?.timeStamp);
  const timeStamp = Number.isFinite(eventTimeStamp)
    ? eventTimeStamp
    : Number.isFinite(nativeTimeStamp)
      ? nativeTimeStamp
      : Number.isFinite(sourceTimeStamp)
        ? sourceTimeStamp
        : Date.now();
  const pointerType = typeof pointerSource.pointerType === 'string' ? pointerSource.pointerType : typeof source.pointerType === 'string' ? source.pointerType : 'mouse';
  const eventButtons = Number(evt?.buttons);
  const nativeButtons = Number(evt?.nativeEvent?.buttons);
  const sourceButtons = Number(source?.buttons);
  const buttons = Number.isFinite(eventButtons)
    ? eventButtons
    : Number.isFinite(nativeButtons)
      ? nativeButtons
      : Number.isFinite(sourceButtons)
        ? sourceButtons
        : 1;
  const eventButton = Number(evt?.button);
  const nativeButton = Number(evt?.nativeEvent?.button);
  const rightButtonDown = Boolean((buttons & 2) || eventButton === 2 || nativeButton === 2);

  const pencilOrientation = getPointerTiltOrientation(pointerSource);
  const tiltX = Number(pointerSource.tiltX);
  const tiltY = Number(pointerSource.tiltY);
  const twist = Number(pointerSource.twist);
  const tangentialPressure = Number(pointerSource.tangentialPressure);
  const altitudeAngle = Number(pointerSource.altitudeAngle);
  const azimuthAngle = Number(pointerSource.azimuthAngle);

  return {
    ...point,
    pressure: Number.isFinite(rawPressure) && rawPressure > 0 ? rawPressure : pointerType === 'mouse' ? 0 : 0.5,
    pointerType,
    timeStamp,
    buttons,
    rightButtonDown,
    calligraphyFollowFlow: rightButtonDown,
    pencilOrientation,
    tiltX: Number.isFinite(tiltX) ? tiltX : 0,
    tiltY: Number.isFinite(tiltY) ? tiltY : 0,
    twist: Number.isFinite(twist) ? twist : 0,
    tangentialPressure: Number.isFinite(tangentialPressure) ? tangentialPressure : 0,
    altitudeAngle: Number.isFinite(altitudeAngle) ? altitudeAngle : undefined,
    azimuthAngle: Number.isFinite(azimuthAngle) ? azimuthAngle : undefined,
  };
}

export function startGrabDrag({ evt, viewportEl, viewRotation, viewPan }) {
  const source = getPointerSource(evt);
  const button = typeof evt?.button === 'number' ? evt.button : 0;

  if (!viewportEl || !source) return null;

  if (typeof evt?.preventDefault === 'function') evt.preventDefault();
  if (typeof evt?.stopPropagation === 'function') evt.stopPropagation();

  if (button === 2) {
    return {
      mode: 'rotate',
      startClientX: source.clientX,
      startClientY: source.clientY,
      startRotation: Number(viewRotation) || 0,
    };
  }

  if (button !== 0) return null;

  return {
    mode: 'pan',
    startClientX: source.clientX,
    startClientY: source.clientY,
    startPanX: Number(viewPan?.x) || 0,
    startPanY: Number(viewPan?.y) || 0,
  };
}

export function continueGrabDrag({ evt, drag, viewportEl, setViewRotation, setViewPan }) {
  const source = getPointerSource(evt);
  if (!drag || !viewportEl || !source) return false;

  if (typeof evt?.preventDefault === 'function') evt.preventDefault();
  if (typeof evt?.stopPropagation === 'function') evt.stopPropagation();

  if (drag.mode === 'rotate') {
    const dx = source.clientX - drag.startClientX;

    if (typeof setViewRotation === 'function') {
      setViewRotation(drag.startRotation + (dx * ROTATION_DRAG_SPEED));
    }

    return true;
  }

  if (drag.mode === 'pan') {
    const dx = source.clientX - drag.startClientX;
    const dy = source.clientY - drag.startClientY;

    if (typeof setViewPan === 'function') {
      setViewPan({
        x: drag.startPanX + dx,
        y: drag.startPanY + dy,
      });
    }

    return true;
  }

  return false;
}

export function stopGrabDrag(grabDragRef) {
  if (grabDragRef) grabDragRef.current = null;
}

export function handleGrabWheelZoom(event, setZoom, clampZoom) {
  if (typeof event?.preventDefault === 'function') event.preventDefault();
  if (typeof event?.stopPropagation === 'function') event.stopPropagation();

  const delta = Number(event?.deltaY) || 0;
  if (!Number.isFinite(delta) || delta === 0) return;

  setZoom((current) => {
    const currentZoom = Number(current) || 1;
    const multiplier = Math.exp(-delta * GRAB_WHEEL_ZOOM_SPEED);
    return clampZoom(currentZoom * multiplier);
  });
}
